/**
 * In-app Guides catalog: product terms, payments, how-tos, and Jet AI.
 * Prices are pulled from live billing constants so Guides stays in sync with Billing.
 */

import { getEvidenceVaultTiers } from './evidenceVault.js';
import { paidBeforeAssignmentClientMessage } from './paidBeforeAssignment.js';
import {
  INTEGRATION_PRICING,
  SEAT_ADDON_PRICING,
  demandFeeVolumeLabel,
  formatDemandFeePrice,
  formatIntegrationPrice,
  formatSeatAddonPrice,
  getDemandFeeTiers,
} from './settingsStore.js';
import { PRIVACY_URL, TERMS_URL, TERMS_VERSION, PRIVACY_VERSION } from './legalTerms.js';

export const GUIDE_TABS = Object.freeze([
  { id: 'billing', label: 'Billing', shortLabel: 'Billing' },
  { id: 'payments', label: 'Payments', shortLabel: 'Pay' },
  { id: 'how-tos', label: "How-to's", shortLabel: 'How-to' },
  { id: 'jet-ai', label: 'Jet AI', shortLabel: 'Jet' },
]);

function vaultTierSummary() {
  return getEvidenceVaultTiers()
    .map((tier) => {
      const price =
        tier.monthlyPrice > 0 ? `$${tier.monthlyPrice}/mo` : 'Included';
      return `${tier.label}: ${tier.gbLimit} GB · ${price}`;
    })
    .join('\n');
}

/**
 * @typedef {{ title: string, body: string, image?: string, caption?: string }} GuideStep
 * @typedef {{
 *   id: string,
 *   tab: string,
 *   title: string,
 *   body: string,
 *   kind?: 'manual' | 'automated',
 *   bullets?: string[],
 *   steps?: GuideStep[]
 * }} GuideSection
 * @returns {{
 *   id: string,
 *   title: string,
 *   summary: string,
 *   tabs: typeof GUIDE_TABS,
 *   sections: GuideSection[]
 * }}
 */
export function getLearnCatalog() {
  const demandTiers = getDemandFeeTiers();
  const connectionPrice = formatIntegrationPrice(INTEGRATION_PRICING.connectionMonthly);
  const seatPrice = formatSeatAddonPrice(SEAT_ADDON_PRICING.extraSeatMonthly);
  const tierBullets = demandTiers.map(
    (tier) =>
      `${tier.label}: ${formatDemandFeePrice(tier.perDemandPrice)} per accepted demand (${demandFeeVolumeLabel(tier)}).`
  );

  return {
    id: 'demandjet-guides',
    title: 'Guides',
    summary:
      'Billing terms, payment rules, step-by-step how-tos with screenshots (manual and automated), and Jet AI.',
    tabs: GUIDE_TABS,
    sections: [
      {
        id: 'per-demand-fees',
        tab: 'billing',
        title: 'Per-demand fees',
        body: 'DemandJet bills per accepted demand on published volume tiers. There is no monthly base demand plan and no share of recovered amounts. The more demands attorneys accept for you each month, the less each one costs.',
        bullets: [
          ...tierBullets,
          'Your tier updates automatically based on your last 3 months’ average, so one slow or busy month won’t swing your price. You’ll get a heads-up before your tier changes.',
        ],
      },
      {
        id: 'charge-timing',
        tab: 'billing',
        title: 'When you are charged',
        body: 'You only pay when an attorney accepts your demand. Creating and sending demands is free.',
        bullets: [
          'When you submit a demand, we place a hold on your card — no charge yet. The charge happens once an attorney accepts.',
          'We never take a share of what you recover.',
        ],
      },
      {
        id: 'unlimited-demands-archive',
        tab: 'billing',
        title: 'Unlimited demands and archive',
        body: 'Active demands, archived demands, and user seats are not capped by a demand plan. Monetization is add-on and per-demand based.',
        bullets: [
          'Create and archive as many demands as you need — archive capacity is unlimited.',
          'File storage for evidence is billed separately through Evidence Locker.',
        ],
      },
      {
        id: 'integration-addon',
        tab: 'billing',
        title: 'Integration add-on',
        body: `Each active accounting connection is a monthly add-on at ${connectionPrice}/mo.`,
        bullets: [
          `Flat rate: ${connectionPrice} per connection per month.`,
          'Applies to connectors such as QuickBooks, Xero, Zoho Books, and other available accounting apps.',
          'Disconnecting a connector stops that connection’s monthly add-on at the next billing cycle (demo accounts update immediately).',
        ],
      },
      {
        id: 'extra-user-addon',
        tab: 'billing',
        title: 'Extra user add-on',
        body: `The account owner seat is included. Each additional user is a monthly add-on at ${seatPrice}/mo.`,
        bullets: [
          `${SEAT_ADDON_PRICING.includedSeats} included seat (owner).`,
          `${seatPrice}/mo for each extra user.`,
          'Inviting a teammate beyond the included owner seat starts the add-on; removing them ends it.',
        ],
      },
      {
        id: 'evidence-locker',
        tab: 'billing',
        title: 'Evidence Locker storage',
        body: 'Evidence files (invoices, packs, demand documents) use GB-based Locker tiers. Uploads are blocked when the Locker is full until you delete files or upgrade.',
        bullets: [
          ...vaultTierSummary().split('\n'),
          'Delete files anytime from Evidence Locker to free storage without changing your plan.',
        ],
      },
      {
        id: 'formal-legal',
        tab: 'billing',
        title: 'Formal legal agreements',
        body: 'Signup requires agreement to DemandJet’s published Terms of Service and Privacy Policy. Those live on the marketing site and are versioned for acceptance tracking.',
        bullets: [
          `Terms of Service (v${TERMS_VERSION}): ${TERMS_URL}`,
          `Privacy Policy (v${PRIVACY_VERSION}): ${PRIVACY_URL}`,
          'This Guides module summarizes product commercial terms; the formal agreements on demandjetapp.com control if there is a conflict.',
        ],
      },
      {
        id: 'paid-before-assignment',
        tab: 'payments',
        title: 'Paid before assignment',
        body: paidBeforeAssignmentClientMessage(),
        bullets: [
          'Applies while the demand is still unassigned (New Case / INTAKE_VALIDATED, no attorney).',
          'Status becomes “Paid before assign”; the matter closes with no per-demand fee.',
          'No attorney is routed for that matter.',
          'If an attorney has already accepted or been assigned, payment resolves as a normal Paid matter, and the per-demand fee still applies and is not refunded.',
        ],
      },
      {
        id: 'recording-payments',
        tab: 'payments',
        title: 'Recording payments',
        body: 'Log recoveries on the demand or Payments list so balances, reports, and attorney status stay accurate.',
        bullets: [
          'Open a demand and use Payments to add a payment with amount, date, and optional proof.',
          'The Payments tab lists portfolio activity across demands.',
          'Currency on each demand defaults from your Profile and can be changed per matter.',
        ],
      },
      {
        id: 'howto-overview',
        tab: 'how-tos',
        title: 'Manual vs automated',
        body: 'Every How-to below walks through a real workflow with screenshots. Manual tasks are what you type or click today. Automated tasks use Jet (or a sync) so you review instead of re-keying. Prefer the automated path whenever it exists.',
        steps: [
          {
            title: 'Pick the path that avoids re-entry',
            body: 'Manual guides show the form-first flow. Automated guides show Jet extract, deep links, and ledger sync so DemandJet fills records for you.',
            image: '/guides/howto-overview.png',
            caption: 'Guides → How-to’s — Manual and Automated guides with real product screenshots.',
          },
        ],
        bullets: [
          'Look for the Manual or Automated badge on each guide.',
          'Screenshots match the Client portal chrome — Demands, Debtors, Evidence, Reports, Messages, Settings.',
          'When Jet is involved, always review extracted values before they hit the form.',
        ],
      },
      {
        id: 'howto-new-demand',
        tab: 'how-tos',
        kind: 'manual',
        title: 'Create a demand (manual)',
        body: 'Use this when you do not have an invoice file to extract, or when you prefer to type the matter yourself. Drafting never bills you — the per-demand fee applies only when an attorney accepts.',
        steps: [
          {
            title: 'Open Demands and start a new matter',
            body: 'Go to Demands in the top nav. Use the blue + control in the header (or open Create Demand from Demands). You land on Create Demand with empty debtor and invoice fields.',
            image: '/guides/howto-demands-new.png',
            caption: 'Demands list — use + to open Create Demand.',
          },
          {
            title: 'Enter debtor, amount, and details',
            body: 'Choose an existing debtor or Enter a new debtor. Fill company, contact, address, email, and amount owed. Review evidence and letter details before you create or send the demand.',
            image: '/guides/howto-create-form.png',
            caption: 'Create Demand form — enter debtor and invoice details manually.',
          },
        ],
        bullets: [
          'Currency defaults from Profile; change it on the amount field when needed.',
          'Prefer the Automated Jet extract guide when you have a PDF or photo of the invoice.',
          'Attach evidence from the demand record or Evidence Locker before sending.',
        ],
      },
      {
        id: 'howto-new-demand-jet',
        tab: 'how-tos',
        kind: 'automated',
        title: 'Create a demand with Jet (automated)',
        body: 'Skip typing the invoice. On Create Demand, open Jet in the sticky footer, upload a clear PDF or photo, review the extract, then apply fields to the form in one tap.',
        steps: [
          {
            title: 'Open Jet on Create Demand',
            body: 'Start New demand as usual, then tap the Jet figure in the sticky footer. Jet offers to fill the form from a chat upload — drop or choose an invoice PDF or photo.',
            image: '/guides/howto-jet-open.png',
            caption: 'Create Demand with Jet open — Ask Jet in the footer, then attach an invoice.',
          },
          {
            title: 'Review extract and Apply to form',
            body: 'Jet returns company, contact, email, amount, invoice #, and related fields. Check every value against the document. Tap Apply to form to move them onto Create Demand, then finish and send when ready.',
            image: '/guides/howto-jet-extract.png',
            caption: 'Jet extract card — review fields, then Apply to form.',
          },
        ],
        bullets: [
          'Prefer a clear, upright PDF or photo — blurry or cropped pages reduce accuracy.',
          'Never send without reviewing the extract; Jet proposes, you confirm.',
          'If extraction fails, check Worker OpenAI configuration or retry with a clearer file — or use the manual Create Demand guide.',
        ],
      },
      {
        id: 'howto-debtors',
        tab: 'how-tos',
        kind: 'manual',
        title: 'Manage debtors (manual)',
        body: 'Debtor profiles hold contacts, account numbers, and linked demands. Keep them accurate so letters and payments resolve to the right party.',
        steps: [
          {
            title: 'Open Debtors and select a record',
            body: 'From the top nav choose Debtors. Scan the list (name, company, account #, open demands). Click a row to open the profile, or Add debtor to create one by hand.',
            image: '/guides/howto-debtors-list.png',
            caption: 'Debtors list — open a record or Add debtor.',
          },
        ],
        bullets: [
          'Edit contacts and account numbers on the debtor record so new demands inherit them.',
          'Linked demands appear on the profile — use them to jump into a matter without searching.',
          'Automated debtor match/merge from invoices is covered on the Jet AI tab (Coming).',
        ],
      },
      {
        id: 'howto-evidence',
        tab: 'how-tos',
        kind: 'manual',
        title: 'Upload evidence (manual)',
        body: 'Evidence Locker stores invoices, contracts, and packs. Upload there or from a demand record, then attach the right files before attorneys accept.',
        steps: [
          {
            title: 'Upload into Evidence Locker',
            body: 'Open Evidence from the top nav. Use Upload files or drop files into the Locker. Files count against your Evidence Locker plan (Included 2 GB, or a paid tier). Delete unused files anytime to free space.',
            image: '/guides/howto-evidence-locker.png',
            caption: 'Evidence Locker — upload, then attach to demands.',
          },
        ],
        bullets: [
          'You can also upload from a demand’s evidence area so the file lands on that matter immediately.',
          'If the Locker is full, delete files or upgrade storage under Settings → Billing.',
          'Jet auto-classify / auto-attach is on the Jet AI tab (Coming).',
        ],
      },
      {
        id: 'howto-record-payment',
        tab: 'how-tos',
        kind: 'manual',
        title: 'Record a payment (manual)',
        body: 'When money arrives, record it against the matching demand so balances and paid-before-assignment rules stay correct.',
        steps: [
          {
            title: 'Record amount, reference, and proof',
            body: 'Open a demand → Payments tab (or start from the Payments list and open a row). Enter payment amount and optional reference #, attach proof if you have it, then Record Payment. Full payoff before attorney accept can trigger paid-before-assignment — see the Payments Guides tab.',
            image: '/guides/howto-payments-record.png',
            caption: 'Demand → Payments — Record Payment with amount, reference, and proof.',
          },
        ],
        bullets: [
          'Attach remittance proof when you have it (PDF or photo).',
          'Jet payment extract/match is on the Jet AI tab (Coming).',
        ],
      },
      {
        id: 'howto-reports-download',
        tab: 'how-tos',
        kind: 'manual',
        title: 'Download a report (manual)',
        body: 'Reports cover Aging, Collections, Pipeline, and custom ledgers. Open any report view and download CSV or PDF for offline review or sharing.',
        steps: [
          {
            title: 'Open a report and choose Download',
            body: 'Go to Reports and open a view (for example Pipeline or Collections). Click Download, then CSV or PDF. PDF exports use DemandJet branding.',
            image: '/guides/howto-reports-download.png',
            caption: 'Report view → Download → CSV or PDF.',
          },
        ],
        bullets: [
          'Aging subtitles describe what is counted (for example since created) — read them before sharing numbers.',
          'Ask-Jet report shortcuts are on the Jet AI tab (Coming).',
        ],
      },
      {
        id: 'howto-messages',
        tab: 'how-tos',
        kind: 'manual',
        title: 'Message on a demand (manual)',
        body: 'Use Messages for attorney/client threads tied to a demand. Enter sends; attachments ride with the composer.',
        steps: [
          {
            title: 'Open inbox or the demand Messages tab',
            body: 'Click the header mail control for the inbox, or open a demand and use its Messages tab. Select a thread, type your reply, attach files if needed, and Send. Unread threads show a notify mark on the demand Messages tab.',
            image: '/guides/howto-messages.png',
            caption: 'Inbox or demand Messages tab — write and Send.',
          },
        ],
        bullets: [
          'Keep matter-specific legal strategy on the demand thread with your attorney.',
          'Jet reply suggestions are on the Jet AI tab (Coming).',
        ],
      },
      {
        id: 'howto-integrations',
        tab: 'how-tos',
        kind: 'manual',
        title: 'Connect accounting (manual)',
        body: 'Connect QuickBooks, Xero, FreshBooks, or other available apps under Settings → Integrations so you can sync open invoices instead of re-typing them. Integration add-on pricing is on the Billing tab.',
        steps: [
          {
            title: 'Connect a provider and sync invoices',
            body: 'Open Settings → Integrations. Choose Connect on the provider card and complete sign-in. After it shows connected, use Sync invoices to import open invoices into DemandJet for demand creation.',
            image: '/guides/howto-integrations.png',
            caption: 'Settings → Integrations → Connect → Sync invoices.',
          },
        ],
        bullets: [
          'Each connection is billed as the integration add-on once activated.',
          'One-click create demand from a ledger invoice is on the Jet AI tab (Coming).',
        ],
      },
      {
        id: 'howto-jet-chat',
        tab: 'how-tos',
        kind: 'automated',
        title: 'Ask Jet for the next step (automated)',
        body: 'Jet in the sticky footer answers pricing and workflow questions and deep-links you to the right page so you spend less time hunting menus.',
        steps: [
          {
            title: 'Open Jet and ask in plain language',
            body: 'Tap the Jet figure in the footer from any Client page. Ask about fees, how to send a demand, QuickBooks, Evidence Locker, or where a setting lives. Use the action chips Jet returns (for example New demand or Open Guides) instead of navigating by hand.',
            image: '/guides/howto-jet-open.png',
            caption: 'Footer Jet — ask, then follow deep links.',
          },
        ],
        bullets: [
          'On Create Demand, Jet also accepts invoice uploads for extract (see Create a demand with Jet).',
          'Jet is not a substitute for attorney advice or formal Terms/Privacy on demandjetapp.com.',
        ],
      },
      {
        id: 'jet-automation-goal',
        tab: 'jet-ai',
        title: 'Automation goal',
        body: 'Jet’s job is to remove manual entry. You should be able to point Jet at invoices, ledgers, emails, and payments — and DemandJet fills records, files evidence, updates status, and drafts the next action with as little typing as possible.',
        bullets: [
          'Prefer upload, sync, or “do this for me” over blank forms.',
          'Jet proposes; you confirm — until you trust a step enough to auto-run.',
          'Live capabilities and roadmap items are listed below so you can plan around zero-keyboard workflows.',
        ],
      },
      {
        id: 'jet-live-now',
        tab: 'jet-ai',
        title: 'Available now',
        body: 'These Jet capabilities are already in the product.',
        bullets: [
          'Footer chat — ask about pricing, workflows, integrations, and where to go next.',
          'Deep links — Jet sends you straight to Guides, Settings, Demands, Debtors, Evidence, Reports, and Messages.',
          'Invoice extract on intake — upload a PDF or photo and Jet fills debtor, amount, and related fields for review.',
          'Apply-to-form — one tap moves extracted fields onto the Create Demand form.',
        ],
      },
      {
        id: 'jet-intake-demands',
        tab: 'jet-ai',
        title: 'Intake and demand creation',
        body: 'End “type the invoice into a form.” Jet should stand up demands from documents and accounting data.',
        bullets: [
          'Live: extract invoice fields into Create Demand.',
          'Coming: batch extract — drop a folder of invoices and open a review queue of draft demands.',
          'Coming: create demand from a QuickBooks / Xero / FreshBooks invoice in one action (no re-keying).',
          'Coming: watch connected ledgers for new open invoices and auto-draft demands.',
          'Coming: fill dispute type, account number, due context, and currency from the source document.',
          'Coming: suggest the right attorney jurisdiction from debtor address and your licensed partners.',
          'Coming: one-click “send when ready” after your confirm checkbox — no re-entry of amount or debtor.',
        ],
      },
      {
        id: 'jet-debtors',
        tab: 'jet-ai',
        title: 'Debtor profiles',
        body: 'Debtor records should assemble themselves from invoices, prior demands, and directory data.',
        bullets: [
          'Coming: create or match a debtor from an uploaded invoice (name, company, email, address).',
          'Coming: merge duplicates Jet finds across spelling variants and account numbers.',
          'Coming: enrich missing fields from linked demands and accounting contacts.',
          'Coming: keep address and contact updates in sync when the ledger contact changes.',
        ],
      },
      {
        id: 'jet-evidence',
        tab: 'jet-ai',
        title: 'Evidence Locker',
        body: 'Filing paperwork should be automatic once a file lands in DemandJet.',
        bullets: [
          'Coming: classify uploads (invoice, contract, statement, photo proof) and tag them without a picker.',
          'Coming: auto-attach the right files to the matching demand and debtor.',
          'Coming: rename and de-dupe files from document contents (invoice #, date, amount).',
          'Coming: warn before Locker capacity fills and suggest what to archive or delete.',
          'Coming: pull invoice PDFs from the accounting connection straight into Evidence.',
        ],
      },
      {
        id: 'jet-letters-messages',
        tab: 'jet-ai',
        title: 'Demand letters and messages',
        body: 'Drafting and follow-ups should start from Jet, not a blank composer.',
        bullets: [
          'Coming: draft or refresh the demand letter from matter facts, amount, and evidence pack.',
          'Coming: suggest reply text on Messages from the thread and demand status.',
          'Coming: queue polite follow-ups when a demand stalls (you approve before send).',
          'Coming: summarize unread threads and highlight what needs your decision.',
          'Coming: translate drafts when debtor language differs from your Profile language.',
        ],
      },
      {
        id: 'jet-payments',
        tab: 'jet-ai',
        title: 'Payments and collections',
        body: 'Payment proof should update balances without spreadsheet work.',
        bullets: [
          'Coming: extract amount, date, and method from a remittance PDF, check photo, or bank screenshot.',
          'Coming: match a payment to the right demand (and split across demands when needed).',
          'Coming: mark paid-before-assignment automatically when full payoff arrives pre-attorney.',
          'Coming: post recovery into the connected ledger when you confirm.',
          'Coming: flag underpayments and propose the remaining balance message.',
        ],
      },
      {
        id: 'jet-pipeline-status',
        tab: 'jet-ai',
        title: 'Pipeline and status',
        body: 'Status changes should follow events, not manual kanban dragging.',
        bullets: [
          'Coming: advance stages from attorney accept, mail tracking, payment, or message milestones.',
          'Coming: surface stalled demands and recommend the next step (nudge, evidence, escalate).',
          'Coming: auto-archive closed/paid matters on your rules (e.g. N days after paid).',
          'Coming: bulk “fix with Jet” for selected demands — same action applied after one confirm.',
        ],
      },
      {
        id: 'jet-reports',
        tab: 'jet-ai',
        title: 'Reports and insights',
        body: 'Ask for the number; Jet builds or opens the report.',
        bullets: [
          'Coming: “Show aging over 90 days” → open or download that report view.',
          'Coming: narrate portfolio KPIs (collected this week, stalled pipeline, evidence gaps).',
          'Coming: schedule emailed report packs without building them by hand.',
          'Coming: anomaly alerts — sudden drop in payments, spike in new intake, missing evidence.',
        ],
      },
      {
        id: 'jet-integrations-settings',
        tab: 'jet-ai',
        title: 'Integrations and settings',
        body: 'Connecting systems and keeping profile defaults current should be conversational.',
        bullets: [
          'Coming: walk through QuickBooks / Xero / FreshBooks connect with fewer clicks.',
          'Coming: map ledger fields to DemandJet once, then reuse for every sync.',
          'Coming: suggest Profile currency from your first invoices.',
          'Coming: invite teammates and assign roles from a plain-language request.',
        ],
      },
      {
        id: 'jet-review-controls',
        tab: 'jet-ai',
        title: 'Review before it runs',
        body: 'Automation still needs a confirm step until you opt into hands-off mode.',
        bullets: [
          'Live today: review extracted invoice fields before they hit the form.',
          'Coming: per-action auto-run toggles (e.g. always attach ledger PDFs; always draft follow-ups for approval).',
          'Coming: activity log of everything Jet changed, with undo where safe.',
          'Jet is not a substitute for attorney advice or formal Terms/Privacy on demandjetapp.com.',
        ],
      },
    ],
  };
}

export function normalizeGuideTab(raw) {
  const id = String(raw || '').trim();
  return GUIDE_TABS.some((tab) => tab.id === id) ? id : GUIDE_TABS[0].id;
}

export function sectionsForGuideTab(tabId, catalog = getLearnCatalog()) {
  const tab = normalizeGuideTab(tabId);
  return (catalog.sections || []).filter((section) => section.tab === tab);
}

export function guideTabForSection(sectionId, catalog = getLearnCatalog()) {
  const section = (catalog.sections || []).find((item) => item.id === String(sectionId || ''));
  return section?.tab ? normalizeGuideTab(section.tab) : GUIDE_TABS[0].id;
}

export function getLearnSectionById(sectionId) {
  const catalog = getLearnCatalog();
  return catalog.sections.find((s) => s.id === String(sectionId || '')) || null;
}

export function listLearnSectionIds() {
  return getLearnCatalog().sections.map((s) => s.id);
}
